import { UserPlus, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { OrgChart } from "@/components/OrgChart";
import { useApp } from "@/lib/AppContext";
import { ApiError, type PersonCard } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { personColour, personInitials } from "@/lib/personAvatar";

/**
 * UI-8 — the people gallery. Everyone the system has heard, as cards carrying the two
 * numbers that say how much it knows about each: the recordings they spoke in, and when the
 * last of those was.
 *
 * A person here is an `entities` row with a `persons` subtype, so adding one is the same
 * call the entity manager makes (UI-6) — this screen is the person-shaped way in, not a
 * second kind of thing.
 *
 * WORLD-10 adds a second view of the same people, the org chart, behind a switch in the header
 * and at its own address (`?view=org-chart`) so the navigator can link to it.
 */
export function PeoplePage() {
  const { people, peopleLoading, peopleProblem } = useApp();
  const [adding, setAdding] = useState(false);
  const [params, setParams] = useSearchParams();
  const view = params.get("view") === "org-chart" ? "org-chart" : "gallery";

  return (
    <div className="flex flex-col gap-[var(--space-6)] px-[var(--space-8)] py-[var(--space-8)]">
      <header className="flex flex-wrap items-start justify-between gap-[var(--space-4)]">
        <div className="flex flex-wrap items-baseline gap-[var(--space-4)]">
          <h1 className="text-[length:var(--text-3xl)] font-extrabold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
            People
          </h1>
          <p className="max-w-[64ch] text-[length:var(--text-md)] text-[var(--text-secondary)]">
            Everyone the system has heard, matched by voice across recordings.
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-[var(--space-3)]">
          <div role="tablist" aria-label="View" className="inline-flex rounded-[var(--radius-md)] border border-[var(--border-subtle)] p-0.5">
            {(
              [
                ["gallery", "Everyone"],
                ["org-chart", "Org chart"],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={view === id}
                onClick={() => setParams(id === "gallery" ? {} : { view: id })}
                className={
                  view === id
                    ? "h-8 rounded-[calc(var(--radius-md)-2px)] bg-[var(--surface-2)] px-3 text-[length:var(--text-sm)] font-medium text-[var(--text-primary)]"
                    : "h-8 rounded-[calc(var(--radius-md)-2px)] px-3 text-[length:var(--text-sm)] text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                }
              >
                {label}
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex h-10 shrink-0 items-center gap-2 rounded-[var(--radius-md)] bg-[var(--primary)] px-4 text-[length:var(--text-sm)] font-medium text-[var(--primary-foreground)] transition hover:brightness-110"
          >
            <UserPlus size={15} strokeWidth={2.5} />
            Add person
          </button>
        </div>
      </header>

      {peopleProblem && (
        <p role="alert" className="text-[length:var(--text-sm)] text-[var(--warning)]">
          {peopleProblem}
        </p>
      )}

      {view === "org-chart" ? (
        <OrgChart />
      ) : peopleLoading ? (
        <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">Loading…</p>
      ) : people.length === 0 ? (
        <Empty onAdd={() => setAdding(true)} />
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-[var(--space-4)]">
          {people.map((person) => (
            <Card key={person.id} person={person} />
          ))}
        </div>
      )}

      {adding && <AddPersonDialog onClose={() => setAdding(false)} />}
    </div>
  );
}

function Card({ person }: { person: PersonCard }) {
  return (
    <Link
      to={`/people/${person.id}`}
      className="flex flex-col gap-2.5 rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] p-[var(--space-4)] transition hover:-translate-y-0.5 hover:border-[var(--border-default)] hover:shadow-[var(--shadow-md)]"
    >
      <span
        aria-hidden
        className="flex h-11 w-11 items-center justify-center rounded-full font-bold text-[var(--text-inverse)]"
        style={{ background: personColour(person.id) }}
      >
        {personInitials(person.name)}
      </span>

      <div>
        <div className="flex items-center gap-1.5">
          <span className="font-bold text-[var(--text-primary)]">{person.name}</span>
          {person.isMe && (
            <span className="rounded-full bg-[var(--vl-life)] px-2 py-0.5 text-[length:var(--text-2xs)] font-semibold text-[var(--text-inverse)]">
              You
            </span>
          )}
        </div>
        {person.role && (
          <div className="mt-px text-[length:var(--text-xs)] text-[var(--text-muted)]">{person.role}</div>
        )}
      </div>

      <div className="flex gap-3 text-[length:var(--text-xs)] text-[var(--text-secondary)]">
        <span>
          {person.meetingCount} {person.meetingCount === 1 ? "meeting" : "meetings"}
        </span>
        {person.lastMeetingOn && <span>{formatDate(person.lastMeetingOn)}</span>}
      </div>
    </Link>
  );
}

/**
 * An organization whose recordings have not been through speaker confirmation yet has no
 * people, and that is a state with a next step rather than a failure — the empty state says
 * both ways one arrives.
 */
function Empty({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="rounded-[var(--radius-xl)] border border-dashed border-[var(--border-default)] p-[var(--space-8)] text-center">
      <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">
        Nobody yet. People arrive when you confirm who spoke in a recording — or you can add one now.
      </p>
      <button
        type="button"
        onClick={onAdd}
        className="mt-[var(--space-4)] inline-flex h-9 items-center gap-2 rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 text-[length:var(--text-sm)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
      >
        <UserPlus size={14} strokeWidth={2} />
        Add person
      </button>
    </div>
  );
}

/**
 * Adding a person is `createEntity({ type: "person" })` — the Server makes the subtype row
 * in the same transaction (0010), so there is no half-made person to clean up. Type is not
 * offered: this is the People screen, and somebody who wanted a customer would be on the
 * entity manager.
 */
function AddPersonDialog({ onClose }: { onClose: () => void }) {
  const { api, refreshPeople, refreshEntities } = useApp();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    nameRef.current?.focus();

    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function submit() {
    const trimmed = name.trim();
    if (trimmed.length === 0 || busy) return;

    setBusy(true);
    setProblem(null);
    try {
      const created = await api.createEntity({ type: "person", name: trimmed, description: "" });
      if (role.trim().length > 0) await api.setPersonRole(created.id, role.trim());

      // Both lists hold persons; leaving the manager's copy stale would show the same
      // person as existing on one screen and not the other.
      refreshPeople();
      refreshEntities();
      navigate(`/people/${created.id}`);
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "The person could not be added.");
      setBusy(false);
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-[color-mix(in_oklch,var(--background)_60%,transparent)] p-4 backdrop-blur-[var(--blur-sm)]"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Add person"
        onClick={(event) => event.stopPropagation()}
        className="w-full max-w-[480px] rounded-[var(--radius-xl)] border border-[var(--border-default)] bg-[var(--surface-1)] p-[var(--space-6)] shadow-[var(--shadow-lg)]"
      >
        <div className="flex items-start justify-between gap-[var(--space-4)]">
          <div>
            <h2 className="text-[length:var(--text-lg)] font-semibold text-[var(--text-primary)]">Add person</h2>
            <p className="mt-1 text-[length:var(--text-sm)] text-[var(--text-secondary)]">
              Somebody the sources talk about, so mentions and voices can resolve to them.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-7 w-7 items-center justify-center rounded-[var(--radius-md)] text-[var(--text-muted)] hover:text-[var(--text-primary)]"
          >
            <X size={15} strokeWidth={2} />
          </button>
        </div>

        <div className="mt-[var(--space-6)] flex flex-col gap-[var(--space-4)]">
          <label className="flex flex-col gap-1.5">
            <span className="text-[length:var(--text-2xs)] uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
              Name
            </span>
            <input
              ref={nameRef}
              value={name}
              onChange={(event) => setName(event.target.value)}
              onKeyDown={(event) => event.key === "Enter" && void submit()}
              placeholder="e.g. Sanne de Groot"
              className="h-9 w-full rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-2)] px-3 text-[length:var(--text-sm)] text-[var(--text-primary)] outline-none focus:border-[var(--ring)]"
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-[length:var(--text-2xs)] uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
              Role
            </span>
            <input
              value={role}
              onChange={(event) => setRole(event.target.value)}
              onKeyDown={(event) => event.key === "Enter" && void submit()}
              placeholder="Optional — e.g. Product, or the customer they work for"
              className="h-9 w-full rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-2)] px-3 text-[length:var(--text-sm)] text-[var(--text-primary)] outline-none focus:border-[var(--ring)]"
            />
          </label>

          {problem && (
            <p role="alert" className="text-[length:var(--text-xs)] text-[var(--warning)]">
              {problem}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="h-8 rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 text-[length:var(--text-sm)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void submit()}
              disabled={busy || name.trim().length === 0}
              className="h-8 rounded-[var(--radius-md)] bg-[var(--primary)] px-3 text-[length:var(--text-sm)] font-medium text-[var(--primary-foreground)] transition hover:brightness-110 disabled:opacity-50"
            >
              {busy ? "Adding…" : "Add person"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
